import React, { useState } from 'react';
import { MapPin, Navigation, Clock, Car, ExternalLink, Map } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const Location = () => {
  const { t } = useLanguage();
  const [mapLoaded, setMapLoaded] = useState(false);

  // Hotel Uma Premium exact coordinates from Google Maps
  // Fusha e, Ulcinj 85360, Montenegro
  // Plus Code: W69R+9X Ulcinj
  const hotelCoordinates = {
    lat: 41.9184254,
    lng: 19.2423905
  };

  const googleMapsUrl = `https://maps.app.goo.gl/XbGsVqjyvhHSfgi59`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=41.9184254,19.2423905`;

  const attractions = [
    {
      id: 1,
      name: t('attractions.velikaPlaza'),
      distance: '1.8 km',
      description: t('attractions.velikaPlazaDesc'),
      mapUrl: 'https://www.google.com/maps/place/Velika+Plaza'
    },
    {
      id: 2,
      name: t('attractions.oldTown'),
      distance: '2.5 km',
      description: t('attractions.oldTownDesc'),
      mapUrl: 'https://www.google.com/maps/place/Old+Town+Ulcinj'
    },
    {
      id: 3,
      name: t('attractions.malaPlaza'),
      distance: '3.2 km',
      description: t('attractions.malaPlazaDesc'),
      mapUrl: 'https://www.google.com/maps/place/Mala+Plaza'
    },
    {
      id: 4,
      name: t('attractions.adaBojana'),
      distance: '12 km',
      description: t('attractions.adaBojanaDesc'),
      mapUrl: 'https://www.google.com/maps/place/Ada+Bojana'
    }
  ];

  return (
    <section id="location" className="py-24 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">{t('location.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-800 mt-3 mb-4">{t('location.title')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t('location.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Interactive Map Section */}
          <div className="space-y-4">
            {/* Google Maps Embed */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[400px] lg:h-[450px] bg-slate-100">
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
                  <div className="text-center">
                    <Map className="w-12 h-12 text-amber-500 mx-auto mb-3 animate-pulse" />
                    <p className="text-slate-500">{t('location.loadingMap')}</p>
                  </div>
                </div>
              )}
              <iframe
                src="https://maps.google.com/maps?q=41.9184254,19.2423905&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hotel Uma Premium Ulcinj - Ulcinj, Montenegro"
                onLoad={() => setMapLoaded(true)}
                className="absolute inset-0"
                data-testid="google-maps-embed"
              />
              
              {/* Map Overlay with Hotel Info */}
              <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
                <div className="bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-lg pointer-events-auto">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Hotel Uma Premium</h4>
                      <p className="text-xs text-slate-500">Ulcinj, Montenegro</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="flex-1 bg-amber-700 hover:bg-amber-800 text-white py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="get-directions-btn"
              >
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer">
                  <Navigation className="w-5 h-5 mr-2" />
                  {t('location.getDirections')}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="flex-1 border-2 border-slate-300 hover:border-amber-500 py-6 rounded-xl transition-all"
                data-testid="open-google-maps-btn"
              >
                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                  <Map className="w-5 h-5 mr-2" />
                  {t('location.openInMaps')}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>

            {/* Address Card */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-5 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-1">{t('location.address')}</h4>
                  <p className="text-slate-300">Fusha e, Ulcinj</p>
                  <p className="text-slate-300">85360 Ulcinj, Montenegro</p>
                  <p className="text-amber-400 text-sm mt-2">Plus Code: W69R+9X Ulcinj</p>
                </div>
              </div>
            </div>
          </div>

          {/* Attractions & Getting Here */}
          <div>
            <h3 className="text-2xl font-serif text-slate-800 mb-6">{t('location.nearbyAttractions')}</h3>
            <div className="space-y-4">
              {attractions.map((attraction, index) => (
                <a
                  key={attraction.id}
                  href={attraction.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-amber-300 cursor-pointer"
                  data-testid={`attraction-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                          <span className="text-amber-600 font-bold group-hover:text-white transition-colors duration-300">
                            {index + 1}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800 group-hover:text-amber-600 transition-colors flex items-center gap-2">
                            {attraction.name}
                            <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </h4>
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm ml-13 pl-13">{attraction.description}</p>
                    </div>
                    <div className="flex items-center gap-1 text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">{attraction.distance}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Getting Here */}
            <div className="mt-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-amber-400" />
                {t('location.gettingHere')}
              </h4>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-3 hover:text-white transition-colors">
                  <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>{t('location.podgoricaAirport')}</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors">
                  <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>{t('location.tiranaAirport')}</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors">
                  <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                  <span>{t('location.dubrovnikAirport')}</span>
                </li>
              </ul>
              
              {/* Airport Shuttle CTA */}
              <div className="mt-5 pt-5 border-t border-slate-700">
                <p className="text-amber-400 text-sm font-medium">
                  ✈️ {t('location.airportShuttleAvailable')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
